import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import t from '../../../labels/labelling-utils';
import { ColorSchema, ComponentSize } from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
import ErrorComponent from '../../atoms/error/error';
import Card from '../../molecules/card/card';
import DataCenterSkeleton from '../../molecules/data-center-skeleton/data-center-skeleton';
import ErrorBoundary from '../error-boundary/error-boundary';
import { makeClassNames } from '../../../utils/styles';
import AdditiveFilter from '../../organisms/additive-filter/additive-filter';
import { objectToDataAttributes } from '../../../utils/tracking-data';
import './card-football-ranking.scss';
import { ComposableList } from '../../organisms';
import { CompetitionTrend } from '../../organisms/competition-ranking-table/competition-ranking-table';
import { IComposableListAccessibleItem } from '../../organisms/composable-list/composable-list';
import { RoundName } from '../../../typings/match';
import { uppercaseFirstLetter } from '../../../utils/string-utils';
import { IMatchTrendItem } from '../../molecules';
import { IRow } from '../../molecules/table/table';

export interface IRankingCardRow extends IRow {
  id?: string;
  games: number;
  lastMatchesTrend?: Array<IMatchTrendItem> | null;
  logoUrl: string;
  logoLink?: React.ReactNode;
  points: number;
  position: number;
  teamName: string;
  teamLink?: React.ReactNode;
  teamUrl?: string | null;
  trend: CompetitionTrend;
}

export interface IRankingCardData extends IComponent {
  roundName: RoundName;
  title?: string | null;
  rows: Array<IRankingCardRow>;
}

export interface ITeam extends IComposableListAccessibleItem {
  id?: string;
  logoUrl: string;
  negativeLogoUrl?: string | null;
  games: number;
  points: number;
  position: number;
  teamName: string;
  teamLink?: React.ReactNode;
  teamUrl?: string | null;
  trend: CompetitionTrend;
  trackingData?: {};
}

export interface IRound {
  roundName: RoundName;
  title?: string | null;
  rows: Array<ITeam>;
}

export interface ICardFootballRanking extends IComponent {
  competitionId?: string;
  customCompetitionName?: string;
  rankingLink?: string;
  groups: Array<IRound>;
  colorSchema?: ColorSchema;
  className?: string;
  ctaMobile?: boolean;
  ctaMobileWhite?: boolean;
  isLoading?: boolean;
  team?: Array<string>;
  size?: ComponentSize;
  trackingData?: {};
  cardCtaTrackingData?: {};
}

const getErrorComponent = () => <ErrorComponent>{t('unavailable.data')}</ErrorComponent>;

const isValidDayList = (groups: IRound[], isLoading: boolean) => isLoading || Boolean(groups && groups.length);

interface IFootballRankingClasses {
  cardRanking: string;
  filterContainer: string;
  cardRankingTeamWrapper: string;
  cardRankingTeamWrapperTrendUp: string;
  cardRankingTeamWrapperTrendStatic: string;
  cardRankingTeamWrapperTrendDown: string;
}

const classNames = (): IFootballRankingClasses =>
  makeClassNames({
    cardRanking: 'card-football-ranking',
    filterContainer: 'card-football-ranking__filter',
    cardRankingTeamWrapper: 'card-football-ranking__team-wrapper',
    cardRankingTeamWrapperTrendUp: 'card-football-ranking__team-wrapper__trend-up',
    cardRankingTeamWrapperTrendStatic: 'card-football-ranking__team-wrapper__trend-static',
    cardRankingTeamWrapperTrendDown: 'card-football-ranking__team-wrapper__trend-down',
  }) as IFootballRankingClasses;

const CardFootballRanking: React.FC<ICardFootballRanking> = ({
  /* istanbul ignore next: default value */ customCompetitionName,
  /* istanbul ignore next: default value */ rankingLink,
  /* istanbul ignore next: default value */ groups,
  /* istanbul ignore next: default value */ isLoading = false,
  trackingData = {},
  cardCtaTrackingData = {},
}) => {
  const cn = classNames();

  const [list, setList] = useState<ITeam[]>(groups?.length > 0 ? groups[0].rows : []);

  useEffect(() => {
    setList(groups?.length > 0 ? groups[0].rows : []);
  }, [groups]);

  const filterList = (groups: IRound[], index: number) => setList(groups[index]?.rows);

  return (
    <aside
      className={cn.cardRanking}
      role="document"
      title="Data card classifica"
      {...objectToDataAttributes(trackingData)}
    >
      <ErrorBoundary hasError={!isValidDayList(groups, isLoading)} errorComponent={getErrorComponent()}>
        <React.Fragment>
          {isLoading ? (
            <DataCenterSkeleton size="small" rowCount={5} />
          ) : (
            <Card
              cta={t('card.view.all')}
              ctaLink={rankingLink}
              title={`${t('ranking.cta')} ${customCompetitionName}`}
              size="small"
              trackingData={cardCtaTrackingData}
              ctaMobile
              ctaMobileWhite
            >
              {groups && groups.length > 1 && (
                <AdditiveFilter
                  filterModel={groups.map((group) => {
                    return { name: `GRUPPO ${group.roundName}` };
                  })}
                  onChange={(index) => filterList(groups, index)}
                />
              )}
              <ComposableList<ITeam>
                headers={{
                  position: t('competition.ranking.th.position'),
                  teamName: t('competition.ranking.th.team'),
                  points: t('competition.ranking.th.points.card')?.charAt(0),
                  games: t('competition.ranking.th.games.card')?.charAt(0),
                }}
                items={list}
                fields={[
                  'position',
                  {
                    prop: 'teamName',
                    render: (data) => (
                      <span
                        className={`${cn.cardRankingTeamWrapper} ${
                          cn[
                            `cardRankingTeamWrapperTrend${uppercaseFirstLetter(
                              data.trend
                            )}` as keyof IFootballRankingClasses
                          ]
                        }`}
                      >
                        <img src={data.logoUrl ?? undefined} aria-hidden /> <span>{data.teamName}</span>
                      </span>
                    ),
                  },
                  'points',
                  'games',
                ]}
                inverted
                aria-label="classifica"
                aria-description={`Classifica ${customCompetitionName}`}
              />
            </Card>
          )}
        </React.Fragment>
      </ErrorBoundary>
    </aside>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  CardFootballRanking.propTypes = {
    customCompetitionName: PropTypes.string,
    rankingLink: PropTypes.string,
    isLoading: PropTypes.bool,
    // @ts-ignore
    groups: PropTypes.exact({
      roundName: PropTypes.string,
      title: PropTypes.string,
      rows: PropTypes.arrayOf(
        PropTypes.shape({
          logoUrl: PropTypes.string,
          games: PropTypes.number,
          points: PropTypes.number,
          position: PropTypes.number,
          teamName: PropTypes.string,
          teamLink: PropTypes.element,
          teamUrl: PropTypes.string,
          trend: PropTypes.string,
        })
      ),
    }),
  };
}

export default CardFootballRanking;
